import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { getLocalStorageItem, clearLocalStorage } from "../utils/Validation";
import { jwtDecode } from "jwt-decode";

interface AuthContextType {
  isAuthenticated: boolean;
  logoutUser: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(
    !!getLocalStorageItem("authToken")
  );

  useEffect(() => {
    const token = getLocalStorageItem("authToken");
    if (!token) {
      setIsAuthenticated(false);
      return;
    }

    try {
      const decoded: { exp: number } = jwtDecode(token);
      const currentTime = Math.floor(Date.now() / 1000);

      if (decoded.exp < currentTime) {
        logoutUser();
      } else {
        const timeout = setTimeout(() => {
          logoutUser();
        }, (decoded.exp - currentTime) * 1000);

        return () => clearTimeout(timeout);
      }
    } catch (error) {
      logoutUser();
    }
  }, []);

  const logoutUser = () => {
    clearLocalStorage();
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, logoutUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
